import styled from '@emotion/styled';
import {Col, Container, Row} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import Link from 'next/link';

import {Logo} from 'Images/icons';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ComponentProps {
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Header'
     */
    testId?: string;
}

/**
 * The `Header` component renders a header section with a logo that links to the homepage.
 * It uses a styled container and a row layout to position the logo.
 * This component is designed to be minimal, providing a clean and focused header.
 *
 * @param props        The component props.
 * @param props.testId An optional unique identifier for testing purposes, defaulting to `'Header'`.
 * @returns A JSX element representing the `Header` component.
 *
 * @example
 * ```tsx
 * <Header testId="mainHeader" />
 * ```
 */
const Header = observer(({testId = 'Header'}: ComponentProps) => (
    <Wrapper data-cy={testId}>
        <StyledContainer>
            <RowWrapper align="center" justify="space-between">
                <Col
                    md="max-content"
                    paddingLeft={{
                        xs: '1rem', md: '0rem'
                    }}
                    xs="auto"
                >
                    <LogoLink href="/">
                        <Logo
                            color1="currentColor"
                            height={30}
                            width={150}
                        />
                    </LogoLink>
                </Col>
            </RowWrapper>
        </StyledContainer>
    </Wrapper>
));

Header.displayName = 'Header';

export {Header};

const Wrapper = styled.header`
    height: 10rem;
    margin-bottom: -10rem;
    position: relative;
    z-index: 100;
`;

const StyledContainer = styled(Container)`
    height: 100%;
`;

const RowWrapper = styled(Row)`
    color: ${({theme}) => theme.colors.primary};
    height: 100%;
`;

const LogoLink = styled(Link)`
    color: ${({theme}) => theme.colors.primary};
    display: contents;
`;